import { METRIC_TYPES } from '@AccessRightSizing/common/constants';
import {
  ACCESS_FLOW_FRAGMENT,
  ACCESS_FLOW_METRICS_FRAGMENT,
  AccessFlowUsageMetricValue_Fragment,
} from '@AccessRightSizing/services/gql';
import { getFragmentData } from '@api/gql';
import { useNavigate } from 'react-router-dom';
import AccessRightSizingItemGrid from './AccessRightSizingItemGrid';
import { Box, Button, Stack, Typography } from '@mui/material';
import { routes } from '@routes';
import { MaterialIcon } from '@components';
import { AccessFlowUsageMetricValue_FragmentFragment, RightSizingReportFieldsFragment } from '@api/gql/graphql';
import { useFlagFixed } from '@hooks';
import { aponoColors, Flag } from '@utils';

interface MetricValueRiskLevel {
  metricValue: string;
  highRiskLevelPercentage?: number;
}

export function AccessRightSizingListItem({ report }: { report: RightSizingReportFieldsFragment }) {
  const navigate = useNavigate();
  const accessFlow = getFragmentData(ACCESS_FLOW_FRAGMENT, report.accessFlow);
  const metrics = getFragmentData(ACCESS_FLOW_METRICS_FRAGMENT, report.metrics);

  const metricValues: { [key: string]: MetricValueRiskLevel } = {
    total_permissions_not_requested: { metricValue: '' },
    total_users_not_requesting: { metricValue: '' },
    avg_access_request_duration: { metricValue: '' },
    total_resources_not_requested: { metricValue: '' },
  };

  const formatPercentage = (unusedValues: AccessFlowUsageMetricValue_FragmentFragment, isAvgDuration = false) => {
    if (unusedValues.defined === 0) return '-';
    const unusedPercentage = parseFloat(((unusedValues.notUsed / unusedValues.defined) * 100).toFixed(1));
    const value = isAvgDuration ? 100 - unusedPercentage : unusedPercentage;
    return isValidPercentage(value) ? `${value.toFixed(1)}%` : '-';
  };

  metrics.forEach((metric) => {
    if (metricValues[metric.metricType]) {
      const unusedValues = getFragmentData(AccessFlowUsageMetricValue_Fragment, metric.currentValue);
      const isAvgDuration = metric.metricType === METRIC_TYPES.avg_access_request_duration;
      metricValues[metric.metricType].metricValue = unusedValues.notUsed
        ? formatPercentage(unusedValues, isAvgDuration)
        : '0%';
      metricValues[metric.metricType].highRiskLevelPercentage = metric.highRiskLevelPercentage || undefined;
    }
  });

  return (
    <Box>
      <AccessRightSizingItemGrid
        accessFlows={accessFlow && <Typography variant="subtitle2">{accessFlow.name}</Typography>}
        unusedResources={<AccessRightSizingItemWithHighRiskLevel value={metricValues.total_resources_not_requested} />}
        unusedPermissions={
          <AccessRightSizingItemWithHighRiskLevel value={metricValues.total_permissions_not_requested} />
        }
        usersNotRequesting={<AccessRightSizingItemWithHighRiskLevel value={metricValues.total_users_not_requesting} />}
        avgDurationRequested={
          <AccessRightSizingItemWithHighRiskLevel value={metricValues.avg_access_request_duration} />
        }
        actions={
          <Button
            onClick={() => navigate(`${routes.AccessRightSizing.path}?access_flow_id=${report.id}`)}
            endIcon={<MaterialIcon symbol="east" weight={800} />}
          >
            More Details
          </Button>
        }
      />
    </Box>
  );
}

function AccessRightSizingItemWithHighRiskLevel({ value }: { value: MetricValueRiskLevel }) {
  const { isEnabled: isRiskScoreEnabled } = useFlagFixed(Flag.SHOW_RISK_SCORE);
  const { metricValue, highRiskLevelPercentage } = value;

  return (
    <Stack display="flex" flexDirection="column" alignItems={'flex-start'} spacing={1}>
      <AccessRightSizingItem metricValue={metricValue} />
      {isRiskScoreEnabled &&
        (highRiskLevelPercentage ? (
          <HighRiskLevel highRiskLevelPercentage={highRiskLevelPercentage} />
        ) : (
          <div style={{ height: '1.3em' }} />
        ))}
    </Stack>
  );
}

function HighRiskLevel({ highRiskLevelPercentage }: { highRiskLevelPercentage: number }) {
  return (
    <Stack direction="row" spacing={0.5}>
      <Typography variant="subtitle2" style={{ color: aponoColors.error[500] }}>
        {highRiskLevelPercentage}%
      </Typography>
      <Typography variant="subtitle2" color={'text.muted'}>
        high risk
      </Typography>
    </Stack>
  );
}

function AccessRightSizingItem({ metricValue }: { metricValue: string }) {
  return (
    <Typography variant="subtitle2" alignSelf={'flex-start'}>
      {metricValue}
    </Typography>
  );
}

function isValidPercentage(value: number) {
  return value >= 0 && value <= 100;
}
